// Файл access_helpers.js

/**
 * Модуль содержит логику доступов для запросов к серверу.
 * Компонент `ACCESS_CACHE_KEY` формирует связанную часть
 * пользовательского интерфейса.
 * Компонент `AUTH_CACHE_KEY` формирует связанную часть пользовательского
 * интерфейса.
 * Доступ к сохранённым настройкам осуществляется в браузере.
 */

import { session_storage } from '../../utils/session_storage';



const ACCESS_CACHE_KEY = 'potale_access_rights';
const AUTH_CACHE_KEY = 'potale_auth_verified_at';
const access_update_storage_key = 'potale_access_updated_at';
const ACCESS_CACHE_TTL = 5 * 60 * 1000;
const ACCESS_ENABLED_VALUES = new Set(['+', '1', 'true', 'да', 'yes']);

const wait = (milliseconds) => new Promise(
    (resolve) => window.setTimeout(resolve, milliseconds)
);

const read_cached_rights = () => {
    try {
        const cached = JSON.parse(
            session_storage.get_item(ACCESS_CACHE_KEY) || 'null'
        );

        if (
            cached
            && Array.isArray(cached.data)
            && Date.now() - Number(cached.saved_at || 0) < ACCESS_CACHE_TTL
        ) {
            return cached.data;
        }

    } catch (error) {
        session_storage.remove_item(ACCESS_CACHE_KEY);
    }

    return null;
};

const has_recent_authorization = (
    max_age = ACCESS_CACHE_TTL,
) => {
    try {
        const verified_at = Number(
            session_storage.get_item(AUTH_CACHE_KEY) || 0
        );

        return verified_at > 0
            && Date.now() - verified_at < max_age;

    } catch (error) {
        return false;
    }
};

const save_cached_rights = (data) => {
    try {
        session_storage.set_item(ACCESS_CACHE_KEY, JSON.stringify({
            saved_at: Date.now(),
            data: Array.isArray(data) ? data : [],
        }));
        session_storage.set_item(AUTH_CACHE_KEY, String(Date.now()));

    } catch (error) {
        // sessionStorage может быть недоступен.
    }
};

const clear_cached_access = () => {
    try {
        session_storage.remove_item(ACCESS_CACHE_KEY);
        session_storage.remove_item(AUTH_CACHE_KEY);

    } catch (error) {
        // sessionStorage может быть недоступен.
    }
};

const build_access_map = (rights_data) => {
    const map = {};

    (rights_data || []).forEach((row) => {
        const code = String(row['Код 2'] || '').split(' - ')[0].trim();
        const flag = String(row.indexCheckbox || '').trim().toLowerCase();

        if (code && ACCESS_ENABLED_VALUES.has(flag)) {
            map[code] = true;
        }
    });

    return map;
};


const build_access_settings = (rights_data) => {
    const row = (rights_data || []).find((item) => (
        String(item?.['Код 2'] || '').split(' - ')[0].trim() === '4.5'
    ));
    const raw_mode = String(
        row?.download_mode
        ?? row?.['Режим скачивания']
        ?? row?.['Вид скачивания']
        ?? 'base'
    ).trim().toLowerCase();

    return {
        download_mode: raw_mode === 'full' ? 'full' : 'base',
    };
};

const request_with_retry = async (request, attempts = 3) => {
    let last_error;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            return await request();

        } catch (error) {
            last_error = error;
            const status = Number(error?.response?.status || 0);

            if (status === 401 || (status > 0 && status < 500)) {
                throw error;
            }

            if (attempt + 1 < attempts) {
                await wait(400 * (attempt + 1));
            }
        }
    }

    throw last_error;
};

const fetch_access_rights = async (api_service) => {
    const rights_response = await request_with_retry(
        () => api_service.df({
            table_name: 'get_df_template_reference',
            params: null,
            timeout: 20000,
        })
    );

    return rights_response?.data || [];
};


export {
    access_update_storage_key,
    build_access_map,
    build_access_settings,
    clear_cached_access,
    fetch_access_rights,
    has_recent_authorization,
    read_cached_rights,
    request_with_retry,
    save_cached_rights,
};
